import { PATRONES_ROTACION } from '../constants/rotaciones';
import { esFeriado } from '../constants/feriadosCL';

// ── Helpers de fecha (trabajan en hora local, sin conversiones UTC) ─────────────

function toLocalMidnight(date) {
  if (typeof date === 'string') {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function diffDays(desde, hasta) {
  return Math.round(
    (toLocalMidnight(hasta).getTime() - toLocalMidnight(desde).getTime()) / 86400000
  );
}

function addDays(date, n) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
}

export { addDays };

// Exportado para que componentes construyan keys de consulta a Supabase.
export function toDateKey(date) {
  const d = toLocalMidnight(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Busca override para una fecha dada. Acepta Map (key = fecha) o Array ({ fecha }).
// Exportado como `overrideDelDia`: media app hacía este `find` a mano en línea
// (Home, SemanaTurnos, la racha), y cada copia era una oportunidad de olvidarse
// de los overrides justo donde importan.
export { findOverride as overrideDelDia };
function findOverride(fecha, overrides) {
  if (!overrides) return null;
  const key = toDateKey(fecha);
  if (overrides instanceof Map) return overrides.get(key) || null;
  if (Array.isArray(overrides))  return overrides.find(o => o.fecha === key) || null;
  return null;
}

// ── calcularTurnoDelDia ─────────────────────────────────────────────────────────
//
// Devuelve el turno que corresponde a `fecha` según la `rotacion`.
//
// rotacion: { patron, fecha_inicio, tipo_institucion }
// override: null | { tipo: 'largo'|'noche'|'diurno'|'libre'|'24' }
//   Si se pasa, fuerza el tipo pero las reglas horarias chilenas se aplican igual.
//   '24' = turno de 24h: 08:00 → 08:00 del día siguiente. Solo existe como
//   override (turno extra puntual), no en los patrones de rotación.
//
// Retorna: { tipo: 'libre' | 'largo' | 'noche' | 'diurno' | '24', horaInicio?, horaFin?, terminaSiguienteDia? }
//
// Reglas de horario LARGO:
//   Hospital público  — L-V no feriado: 08:00-20:00 / Sáb, Dom, feriado: 09:00-20:00
//   Clínica privada   — L-Sáb no feriado: 08:00-20:00 / Dom, feriado: 09:00-20:00
//
// Reglas de horario NOCHE — la noche termina cuando empieza el largo del día siguiente:
//   Hospital público  — siguiente L-V no feriado: 08:00  |  siguiente Sáb/Dom/feriado: 09:00
//   Clínica privada   — siguiente L-Sáb no feriado: 08:00  |  siguiente Dom/feriado: 09:00

export function calcularTurnoDelDia(fecha, rotacion, override = null) {
  const d        = toLocalMidnight(fecha);
  const isPublico = rotacion?.tipo_institucion === 'publico';
  const dow      = d.getDay(); // 0=Dom, 6=Sáb

  // ── Override fuerza el tipo, pero las reglas de horario chilenas se mantienen ──
  if (override) {
    if (override.tipo === 'libre') return { tipo: 'libre' };

    if (override.tipo === 'diurno') {
      return { tipo: 'diurno', horaInicio: '08:00', horaFin: '17:00' };
    }

    // Turno de 24h: bloque continuo 08:00 → 08:00 del día siguiente. A
    // diferencia del largo/noche, no aplica el corrimiento a 09:00 de fin de
    // semana: el turno de 24h es un bloque fijo. terminaSiguienteDia lo marca
    // como cruce de día (estadoDelDia y las notificaciones ya lo respetan).
    if (override.tipo === '24') {
      return { tipo: '24', horaInicio: '08:00', horaFin: '08:00', terminaSiguienteDia: true };
    }

    if (override.tipo === 'largo') {
      const starts09 = isPublico
        ? dow === 0 || dow === 6 || esFeriado(d)
        : dow === 0 || esFeriado(d);
      return { tipo: 'largo', horaInicio: starts09 ? '09:00' : '08:00', horaFin: '20:00' };
    }

    if (override.tipo === 'noche') {
      const sig    = addDays(d, 1);
      const sigDow = sig.getDay();
      const ends09 = isPublico
        ? (sigDow === 6 || sigDow === 0 || esFeriado(sig))
        : (sigDow === 0 || esFeriado(sig));
      return { tipo: 'noche', horaInicio: '20:00', horaFin: ends09 ? '09:00' : '08:00', terminaSiguienteDia: true };
    }
  }

  // ── Cálculo normal basado en patrón ──────────────────────────────────────────
  if (!rotacion?.patron || !rotacion?.fecha_inicio) return { tipo: 'libre' };

  const inicio = toLocalMidnight(rotacion.fecha_inicio);
  const diff   = diffDays(inicio, d);

  if (diff < 0) return { tipo: 'libre' };

  const patron = PATRONES_ROTACION.find(p => p.id === rotacion.patron);
  if (!patron || !patron.secuencia) return { tipo: 'libre' };

  const codigo = patron.secuencia[diff % patron.secuencia.length];

  if (codigo === '_') return { tipo: 'libre' };

  if (codigo === 'D') {
    return { tipo: 'diurno', horaInicio: '08:00', horaFin: '17:00' };
  }

  if (codigo === 'L') {
    const starts09 = isPublico
      ? dow === 0 || dow === 6 || esFeriado(d)
      : dow === 0 || esFeriado(d);
    return { tipo: 'largo', horaInicio: starts09 ? '09:00' : '08:00', horaFin: '20:00' };
  }

  if (codigo === 'N') {
    const sig    = addDays(d, 1);
    const sigDow = sig.getDay();
    const ends09 = isPublico
      ? (sigDow === 6 || sigDow === 0 || esFeriado(sig))
      : (sigDow === 0 || esFeriado(sig));
    return { tipo: 'noche', horaInicio: '20:00', horaFin: ends09 ? '09:00' : '08:00', terminaSiguienteDia: true };
  }

  return { tipo: 'libre' };
}

// ── obtenerProximoTurno ─────────────────────────────────────────────────────────
//
// Retorna { fecha, turno } del próximo día con turno en los siguientes 30 días,
// o null si no hay rotación o ningún día tiene turno en ese período.
// overrides: Map | Array | null

export function obtenerProximoTurno(fechaActual, rotacion, overrides = null) {
  if (!rotacion) return null;
  const hoy = toLocalMidnight(fechaActual);
  for (let i = 1; i <= 30; i++) {
    const dia      = addDays(hoy, i);
    const override = findOverride(dia, overrides);
    const turno    = calcularTurnoDelDia(dia, rotacion, override);
    if (turno.tipo !== 'libre') return { fecha: dia, turno };
  }
  return null;
}

// ── esPostNoche ──────────────────────────────────────────────────────────────────
//
// Retorna true si el día anterior fue un turno que cruza la noche (noche o
// turno de 24h) — el usuario llega a casa la mañana de ESTE día.
// overrides: Map | Array | null — necesario para respetar overrides de ayer.

export function esPostNoche(fecha, rotacion, overrides = null) {
  if (!rotacion) return false;
  const ayer     = addDays(toLocalMidnight(fecha), -1);
  const override = findOverride(ayer, overrides);
  const tipoAyer = calcularTurnoDelDia(ayer, rotacion, override).tipo;
  return tipoAyer === 'noche' || tipoAyer === '24';
}

// ── inicioDeSemana ──────────────────────────────────────────────────────────────
// Lunes de la semana de `fecha`. Devuelve la fecha a medianoche local.
export function inicioDeSemana(fecha) {
  const d = toLocalMidnight(fecha);
  const dow = d.getDay(); // 0 = domingo
  const diff = dow === 0 ? -6 : 1 - dow; // mover a lunes
  return addDays(d, diff);
}

// ── estadoDelDia ────────────────────────────────────────────────────────────────
// Estado horario del turno actual relativo a `now`.
//   'libre'   — turno.tipo === 'libre'
//   'pre'     — now < horaInicio
//   'durante' — now entre horaInicio y horaFin (cuenta cruce de noche)
//   'post'    — now > horaFin
export function estadoDelDia(turno, now = new Date()) {
  if (!turno || turno.tipo === 'libre') return 'libre';
  if (!turno.horaInicio || !turno.horaFin) return 'libre';
  const [hI, mI] = turno.horaInicio.split(':').map(Number);
  const [hF, mF] = turno.horaFin.split(':').map(Number);
  const inicioMin = hI * 60 + mI;
  const finMin    = hF * 60 + mF;
  const nowMin    = now.getHours() * 60 + now.getMinutes();
  // Turno noche que cruza días: horaFin < horaInicio
  if (turno.terminaSiguienteDia || finMin < inicioMin) {
    if (nowMin >= inicioMin) return 'durante'; // tarde-noche del día start
    return 'pre'; // mañana antes del start (o post si era ayer)
  }
  if (nowMin < inicioMin) return 'pre';
  if (nowMin > finMin)    return 'post';
  return 'durante';
}

// ── diaDeRotacion ───────────────────────────────────────────────────────────────
// N (1-indexed) del día de rotación según fecha_inicio.
export function diaDeRotacion(rotacion, fecha = new Date()) {
  if (!rotacion?.fecha_inicio) return null;
  const inicio = toLocalMidnight(rotacion.fecha_inicio);
  const hoy = toLocalMidnight(fecha);
  const n = diffDays(inicio, hoy) + 1;
  return n >= 1 ? n : null;
}

